/**
 * Lightweight Joyride provider for assistant-triggered micro-tours.
 * Reuses TourTooltip from the demo tour.
 * Only one tour (demo or micro) can run at a time.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Joyride,
  ACTIONS,
  EVENTS,
  STATUS,
  type EventData,
  type Controls,
} from 'react-joyride'
import { useNavigate } from '@tanstack/react-router'
import { useDemoTour } from '@/features/demo/tour/useDemoTour'
import TourTooltip from '@/features/demo/tour/TourTooltip'
import { microTours, type MicroTourStep } from './microTourSteps'

function waitForElement(
  selector: string,
  timeout = 3000,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }
    const start = Date.now()
    const interval = setInterval(() => {
      const el = document.querySelector(selector)
      if (el) {
        clearInterval(interval)
        resolve(el)
      } else if (Date.now() - start > timeout) {
        clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

export interface MicroTourContextValue {
  startMicroTour: (tourKey: string) => void
  stopMicroTour: () => void
  isActive: boolean
}

const MicroTourContext = createContext<MicroTourContextValue>({
  startMicroTour: () => {},
  stopMicroTour: () => {},
  isActive: false,
})

export function useMicroTour() {
  return useContext(MicroTourContext)
}

export default function MicroTourProvider({
  children,
}: {
  children: ReactNode
}) {
  const navigate = useNavigate()
  const demoTour = useDemoTour()

  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [steps, setSteps] = useState<MicroTourStep[]>([])
  const navigatingRef = useRef(false)
  const stepsRef = useRef<MicroTourStep[]>([])
  // Sync steps to ref for use in callbacks
  useEffect(() => {
    stepsRef.current = steps
  }, [steps])

  const startMicroTour = useCallback(
    (tourKey: string) => {
      // Don't start if demo tour is running
      if (demoTour.isActive) return

      const tourSteps = microTours[tourKey]
      if (!tourSteps?.length) return

      setSteps(tourSteps)
      stepsRef.current = tourSteps

      // Navigate to the first step's page if needed
      const firstStep = tourSteps[0]
      navigate({ to: firstStep.page }).then(() => {
        waitForElement(firstStep.target as string, 5000).then((el) => {
          if (el) {
            setStepIndex(0)
            setRun(true)
          }
        })
      })
    },
    [demoTour.isActive, navigate],
  )

  const stopMicroTour = useCallback(() => {
    setRun(false)
    setSteps([])
    setStepIndex(0)
  }, [])

  const handleEvent = useCallback(
    async (data: EventData, _controls: Controls) => {
      const { action, index, type, status } = data
      const currentSteps = stepsRef.current

      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        setRun(false)
        return
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1
        if (nextIndex < 0) return
        if (nextIndex >= currentSteps.length) {
          setRun(false)
          return
        }

        const nextStep = currentSteps[nextIndex]
        const currentStep = currentSteps[index]

        if (nextStep.page !== currentStep.page) {
          navigatingRef.current = true
          setRun(false)
          await navigate({ to: nextStep.page })
          await waitForElement(nextStep.target as string, 5000)
          navigatingRef.current = false
          setStepIndex(nextIndex)
          setRun(true)
        } else {
          setStepIndex(nextIndex)
        }
      }
    },
    [navigate],
  )

  const value = useMemo(
    () => ({ startMicroTour, stopMicroTour, isActive: run }),
    [startMicroTour, stopMicroTour, run],
  )

  return (
    <MicroTourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          continuous
          onEvent={handleEvent}
          tooltipComponent={TourTooltip}
          scrollToFirstStep
          options={{
            overlayColor: 'rgba(0, 0, 0, 0.45)',
            overlayClickAction: false,
            zIndex: 1400,
            spotlightRadius: 12,
            buttons: ['back', 'close', 'primary', 'skip'],
          }}
        />
      )}
    </MicroTourContext.Provider>
  )
}
